import { flushSync } from 'react-dom'
import type React from "react"
import { useState, useRef, useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { Home, CheckSquare, Users, Calendar, UsersRound, LogOut, UserPlus, StickyNote, Files, Mail } from "lucide-react"
import { UserAvatar } from "components/ui/avatar"
import { cn } from "lib/utils"
import { useUser } from "contexts/UserContext"
import { logoutUser } from "services/users"

interface NavItem {
  icon: React.ElementType
  label: string
  path: string
  module: string | null // null = sempre visibile, string = richiede modulo abilitato
}

// Moduli core - sempre visibili
const CORE_NAV_ITEMS: NavItem[] = [
  { icon: Home, label: "Dashboard", path: "/dashboard-new", module: null },
  { icon: CheckSquare, label: "Task", path: "/tasks", module: null },
  { icon: Users, label: "Contatti", path: "/contacts", module: null },
  { icon: Files, label: "Documenti", path: "/documents", module: null },
  { icon: Calendar, label: "Agenda", path: "/agenda", module: null },
  { icon: UsersRound, label: "Team", path: "/team", module: null },
]

// Plugin - visibili solo se abilitati
const PLUGIN_NAV_ITEMS: NavItem[] = [
  { icon: UserPlus, label: "Leads", path: "/leads", module: "leads" },
  { icon: StickyNote, label: "Note", path: "/notes", module: "notes" },
  { icon: Mail, label: "Mail", path: "/mail/inbox", module: "emails" },
]

interface SidebarNavProps {
  currentUser?: {
    first_name?: string
    last_name?: string
    email?: string
    username?: string
    profile_photo_url?: string | null
  } | null
  className?: string
  onNavigate?: () => void
}

export function SidebarNav({ currentUser, className, onNavigate }: SidebarNavProps) {
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { user, setUser } = useUser()

  // Filtra gli item della nav in base ai moduli abilitati
  const enabledModules = user?.enabled_modules || {}

  // I moduli core sono sempre visibili
  const visibleCoreItems = CORE_NAV_ITEMS

  // I plugin sono visibili solo se abilitati
  const visiblePluginItems = useMemo(() => {
    return PLUGIN_NAV_ITEMS.filter((item) => {
      if (item.module === null) return true
      return enabledModules[item.module] === true
    })
  }, [enabledModules])

  // Costruisci nome utente da visualizzare
  const userName = currentUser
    ? (currentUser.first_name && currentUser.last_name
        ? `${currentUser.first_name} ${currentUser.last_name}`
        : currentUser.username || 'Utente')
    : 'Utente'

  const userEmail = currentUser?.email || 'user@example.com'

  const handleLogout = async () => {
    // Notifica il backend per registrare il logout (UserActivityLog)
    try {
      await logoutUser()
    } catch (e) {
      // Non bloccare il logout se la chiamata fallisce
      console.warn('logout backend failed', e)
    }
    // Rimuovi i token di accesso
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    flushSync(() => setUser(null))
    // Reindirizza alla pagina di login
    window.location.href = '/login'
  }

  // Chiudi menu quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  // Chiudi menu con Esc
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [isMenuOpen])

  return (
    <aside
      className={cn("w-56 shrink-0 flex-none backdrop-blur-sm border-r flex flex-col", className)}
      style={{
        backgroundColor: "var(--hexa-sidebar-bg)",
        borderColor: "var(--hexa-sidebar-border)",
      }}
    >
      {/* Logo area */}
      <div className="h-12 flex items-center px-4">
        <div className="flex items-center gap-2">
          <img
            src="/images/hexa-logo.png"
            alt="HEXA"
            width={24}
            height={24}
            className="h-6 w-auto object-contain shrink-0"
          />
          <span className="font-semibold text-base" style={{ color: "var(--hexa-text-primary)" }}>
            HEXA
          </span>
        </div>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 px-2.5 py-4 space-y-0.5">
        {/* Core modules */}
        {visibleCoreItems.map((item) => (
          <NavLink
            key={item.label}
            icon={item.icon}
            label={item.label}
            to={item.path}
            onNavigate={onNavigate}
            active={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
          />
        ))}

        {/* Plugin section - only show if there are visible plugins */}
        {visiblePluginItems.length > 0 && (
          <>
            <div className="pt-3 pb-1.5 px-2.5">
              <div
                className="h-px mb-2"
                style={{ backgroundColor: "var(--hexa-border)" }}
              />
              <span
                className="text-[10px] font-medium uppercase tracking-wider"
                style={{ color: "var(--hexa-text-muted)" }}
              >
                Plugin
              </span>
            </div>
            {visiblePluginItems.map((item) => (
              <NavLink
                key={item.label}
                icon={item.icon}
                label={item.label}
                to={item.path}
                onNavigate={onNavigate}
                active={location.pathname === item.path}
              />
            ))}
          </>
        )}
      </nav>

      {/* User profile with dropdown menu */}
      <div className="p-3 border-t" style={{ borderColor: "var(--hexa-border)" }} ref={menuRef}>
        <div className="relative">
          {/* User profile trigger button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "var(--hexa-sidebar-item-hover)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent"
            }}
          >
            <UserAvatar
              firstName={currentUser?.first_name}
              lastName={currentUser?.last_name}
              username={currentUser?.username}
              imageUrl={currentUser?.profile_photo_url || undefined}
              size="md"
              showOnlineIndicator
              isOnline
            />
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-medium truncate" style={{ color: "var(--hexa-text-primary)" }}>
                {userName}
              </p>
              <p className="text-[10px] truncate" style={{ color: "var(--hexa-text-tertiary)" }}>
                {userEmail}
              </p>
            </div>
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div
              role="menu"
              className="absolute bottom-full left-0 right-0 mb-2 border rounded-lg shadow-lg overflow-hidden"
              style={{
                backgroundColor: "var(--hexa-surface)",
                borderColor: "var(--hexa-border)",
              }}
            >
              <button
                role="menuitem"
                onClick={() => {
                  setIsMenuOpen(false)
                  handleLogout()
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 transition-colors text-left"
                style={{ color: "var(--hexa-text-secondary)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "var(--hexa-sidebar-item-hover)"
                  e.currentTarget.style.color = "var(--hexa-text-primary)"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent"
                  e.currentTarget.style.color = "var(--hexa-text-secondary)"
                }}
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

interface NavLinkProps {
  icon: React.ElementType
  label: string
  to: string
  active?: boolean
  onNavigate?: () => void
}

function NavLink({ icon: Icon, label, to, active = false, onNavigate }: NavLinkProps) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <Link
      to={to}
      className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-colors text-left text-sm"
      style={{
        backgroundColor: active
          ? "var(--hexa-sidebar-item-active)"
          : isHovered
          ? "var(--hexa-sidebar-item-hover)"
          : "transparent",
        color: active ? "var(--hexa-text-primary)" : "var(--hexa-text-secondary)",
      }}
      onMouseEnter={() => !active && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onNavigate}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="font-medium truncate">{label}</span>
    </Link>
  )
}
